import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { Scan } from '../../types';
import { SeverityDot } from '../ui/SeverityBadge';

interface ScanComparisonCardProps {
  currentScan: Scan;
  previousScan: Scan;
}

interface ScanComparison {
  issuesFixed: number;
  newIssues: number;
  netChange: number;
  scoreChange: number;
  criticalChange: number;
  seriousChange: number;
  moderateChange: number;
  minorChange: number;
}

function compareTwoScans(current: Scan, previous: Scan): ScanComparison {
  const curr = current.results?.uniqueIssues ?? current.results?.totalIssues ?? { critical: 0, serious: 0, moderate: 0, minor: 0, total: 0 };
  const prev = previous.results?.uniqueIssues ?? previous.results?.totalIssues ?? { critical: 0, serious: 0, moderate: 0, minor: 0, total: 0 };

  return {
    issuesFixed: Math.max(0, prev.total - curr.total),
    newIssues: Math.max(0, curr.total - prev.total),
    netChange: curr.total - prev.total,
    scoreChange: (current.results?.overallScore ?? 0) - (previous.results?.overallScore ?? 0),
    criticalChange: curr.critical - prev.critical,
    seriousChange: curr.serious - prev.serious,
    moderateChange: curr.moderate - prev.moderate,
    minorChange: curr.minor - prev.minor,
  };
}

function ChangeIndicator({ value, inverse = false }: { value: number; inverse?: boolean }) {
  // For issues, negative is good (fewer issues). For score, positive is good.
  const isGood = inverse ? value > 0 : value < 0;
  const isBad = inverse ? value < 0 : value > 0;

  if (value === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-gray-500">
        <Minus size={14} />
        <span>0</span>
      </span>
    );
  }

  if (isGood) {
    return (
      <span className="inline-flex items-center gap-1 text-green-600">
        <ArrowDown size={14} />
        <span>{Math.abs(value)}</span>
      </span>
    );
  }

  if (isBad) {
    return (
      <span className="inline-flex items-center gap-1 text-red-600">
        <ArrowUp size={14} />
        <span>+{Math.abs(value)}</span>
      </span>
    );
  }

  return null;
}

function ScoreChangeIndicator({ value }: { value: number }) {
  if (value === 0) {
    return <span className="text-gray-500">No change</span>;
  }

  if (value > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-green-600 font-medium">
        <ArrowUp size={14} />
        +{value} points
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-red-600 font-medium">
      <ArrowDown size={14} />
      {value} points
    </span>
  );
}

export function ScanComparisonCard({ currentScan, previousScan }: ScanComparisonCardProps) {
  const comparison = compareTwoScans(currentScan, previousScan);

  const currentScore = currentScan.results?.overallScore ?? 0;
  const previousScore = previousScan.results?.overallScore ?? 0;

  const isImprovement = comparison.netChange < 0;
  const isRegression = comparison.netChange > 0;

  return (
    <div className="card h-full">
      <h3 className="text-body font-semibold mb-4">Latest vs Previous</h3>

      {/* Net Change Banner */}
      <div
        className={`rounded-lg p-3 mb-4 ${
          isImprovement
            ? 'bg-green-50 border border-green-200'
            : isRegression
            ? 'bg-red-50 border border-red-200'
            : 'bg-gray-50 border border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between">
          <span className="text-body-sm text-gray-600">Net Change</span>
          <span
            className={`text-body font-semibold ${
              isImprovement ? 'text-green-700' : isRegression ? 'text-red-700' : 'text-gray-600'
            }`}
          >
            {comparison.netChange === 0
              ? 'No change'
              : comparison.netChange > 0
              ? `+${comparison.netChange} issues`
              : `${comparison.netChange} issues`}
          </span>
        </div>
      </div>

      {/* Issues Fixed / New Issues */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className="text-heading-3 font-bold text-green-600">{comparison.issuesFixed}</div>
          <div className="text-caption text-green-700">Fixed</div>
        </div>
        <div className="bg-red-50 rounded-lg p-3 text-center">
          <div className="text-heading-3 font-bold text-red-600">{comparison.newIssues}</div>
          <div className="text-caption text-red-700">New</div>
        </div>
      </div>

      {/* Score Change */}
      <div className="border-t border-gray-100 pt-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-body-sm text-gray-600">Score</span>
          <span className="text-body-sm">
            {previousScore} → {currentScore}
          </span>
        </div>
        <ScoreChangeIndicator value={comparison.scoreChange} />
      </div>

      {/* Severity Breakdown */}
      <div className="border-t border-gray-100 pt-4">
        <div className="text-body-sm text-gray-600 mb-3">By Severity</div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SeverityDot severity="critical" size="sm" />
              <span className="text-body-sm">Critical</span>
            </div>
            <ChangeIndicator value={comparison.criticalChange} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SeverityDot severity="serious" size="sm" />
              <span className="text-body-sm">Serious</span>
            </div>
            <ChangeIndicator value={comparison.seriousChange} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SeverityDot severity="moderate" size="sm" />
              <span className="text-body-sm">Moderate</span>
            </div>
            <ChangeIndicator value={comparison.moderateChange} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SeverityDot severity="minor" size="sm" />
              <span className="text-body-sm">Minor</span>
            </div>
            <ChangeIndicator value={comparison.minorChange} />
          </div>
        </div>
      </div>
    </div>
  );
}
